import { useEffect, useRef, useState } from 'react';
import { validateArenaDef, type ArenaDef, type TilesetDef } from '@archon3d/core';
import { ArenaPreview } from './ArenaPreview';
import { paintArena, rotateStamp, stampsOf, tileColor, tileLabel } from './arenaEditing';
import { editorShortcut } from './shortcuts';

export function ArenaEditor({
  arena,
  tileset,
  readOnly,
  onChange,
  onBack,
}: {
  arena: ArenaDef;
  tileset: TilesetDef;
  readOnly: boolean;
  onChange: (a: ArenaDef) => void;
  onBack: () => void;
}) {
  const [brush, setBrush] = useState('grass');
  const [turns, setTurns] = useState(0);
  const [preview, setPreview] = useState(false);
  const [notice, setNotice] = useState('');
  const [hover, setHover] = useState<[number, number] | null>(null);
  const stamps = stampsOf(tileset);
  const footprint = rotateStamp(stamps[brush] ?? [[brush]], turns);
  const [past, setPast] = useState<ArenaDef[]>([]),
    [future, setFuture] = useState<ArenaDef[]>([]);
  /** The arena as last changed: a drag changes it faster than it is drawn again. */
  const latest = useRef(arena);
  latest.current = arena;
  /**
   * The run of edits under way, kept as one undo step: the name typed a letter at a time, or the
   * squares painted in one press and the drag after it (editor:bugs#8, editor:quality#12).
   */
  const run = useRef<string | null>(null);
  const strokes = useRef(0);
  /** The square the press under way painted last, while the button is held. */
  const pressed = useRef<string | null>(null);
  /** A single block paints every square a drag crosses; a group or a spawn goes where pressed. */
  const drags = !brush.startsWith('spawn') && !stamps[brush];
  const change = (next: ArenaDef, key: string | null = null) => {
    if (readOnly) return;
    const before = latest.current;
    if (JSON.stringify(next) === JSON.stringify(before)) return;
    if (key === null || key !== run.current) setPast((p) => [...p.slice(-49), before]);
    run.current = key;
    setFuture([]);
    latest.current = next;
    onChange(next);
    setNotice('');
  };
  const cell = (x: number, y: number) => {
    if (readOnly) return;
    const arena = latest.current;
    try {
      if (brush.startsWith('spawn')) {
        const next = structuredClone(arena);
        const i = Number(brush.slice(-1));
        if (next.spawns.some(([sx, sy], j) => j !== i && sx === x && sy === y))
          throw new Error('Fighters need different spawn squares.');
        next.spawns[i] = [x, y];
        change(validateArenaDef(next, { [tileset.id]: tileset }), `stroke:${strokes.current}`);
      } else
        change(
          paintArena(arena, tileset, x, y, rotateStamp(stamps[brush] ?? [[brush]], turns)),
          `stroke:${strokes.current}`,
        );
    } catch (e) {
      setNotice((e as Error).message);
    }
  };
  /** A press on a square: a stroke of its own. */
  const press = (x: number, y: number) => {
    strokes.current += 1;
    cell(x, y);
  };
  /** Undo or redo: the arena `from` holds last comes back, and the current one goes to `to`. */
  const step = (from: ArenaDef[], to: ArenaDef[]) => {
    const a = from.at(-1);
    if (!a) return null;
    const current = latest.current;
    run.current = null;
    latest.current = a;
    onChange(a);
    return [from.slice(0, -1), [...to, current]] as const;
  };
  const undo = () => {
    const moved = step(past, future);
    if (!moved) return;
    setPast([...moved[0]]);
    setFuture([...moved[1]]);
  };
  const redo = () => {
    const moved = step(future, past);
    if (!moved) return;
    setFuture([...moved[0]]);
    setPast([...moved[1]]);
  };
  // Ctrl+Z and Ctrl+Shift+Z or Ctrl+Y undo and redo the arena while it is open (editor:quality#14).
  const onShortcut = useRef<(e: KeyboardEvent) => void>(() => undefined);
  onShortcut.current = (e) => {
    const shortcut = editorShortcut(e);
    if (readOnly || (shortcut !== 'undo' && shortcut !== 'redo')) return;
    e.preventDefault();
    if (shortcut === 'undo') undo();
    else redo();
  };
  useEffect(() => {
    const listener = (e: KeyboardEvent) => onShortcut.current(e);
    window.addEventListener('keydown', listener);
    return () => window.removeEventListener('keydown', listener);
  }, []);
  return (
    <section className="arena-editor">
      <div className="editor-head">
        <h3>Arena map editor {readOnly ? '· read-only' : ''}</h3>
        <button onClick={onBack}>Back to game board</button>
      </div>
      <div className="menu-actions">
        <label>
          Name{' '}
          <input
            aria-label="Arena name"
            value={arena.name}
            disabled={readOnly}
            onChange={(e) => change({ ...latest.current, name: e.target.value }, 'name')}
          />
        </label>
        <button onClick={() => setPreview(!preview)}>
          {preview ? 'Edit squares' : '3D preview'}
        </button>
        <button disabled={readOnly || !past.length} title="Ctrl+Z" onClick={undo}>
          Undo arena
        </button>
        <button disabled={readOnly || !future.length} title="Ctrl+Shift+Z or Ctrl+Y" onClick={redo}>
          Redo arena
        </button>
      </div>
      {readOnly && (
        <p className="hint">
          Default Arena is protected. Make a copy in the arena list to create your own variation.
        </p>
      )}
      {notice && <p role="alert">{notice}</p>}
      {preview ? (
        <ArenaPreview arena={arena} tileset={tileset} />
      ) : (
        <div className="editor-body">
          <div className="editor-stage">
            <svg
              className={`arena-grid${drags && !readOnly ? ' drag' : ''}`}
              viewBox={`0 0 ${arena.width} ${arena.height}`}
              role="group"
              aria-label="Arena squares"
              onMouseLeave={() => setHover(null)}
              onPointerDown={(e) => {
                if (e.button !== 0 || readOnly) return;
                const at = (e.target as Element)
                  .closest('[data-arena-cell]')
                  ?.getAttribute('data-arena-cell');
                if (!at) return;
                pressed.current = at;
                const [x, y] = at.split(',').map(Number) as [number, number];
                press(x, y);
              }}
              onPointerMove={(e) => {
                if (pressed.current === null) return;
                if (!drags || (e.buttons & 1) === 0) {
                  pressed.current = null;
                  return;
                }
                const at = document
                  .elementFromPoint(e.clientX, e.clientY)
                  ?.closest('[data-arena-cell]')
                  ?.getAttribute('data-arena-cell');
                if (!at || at === pressed.current) return;
                pressed.current = at;
                const [x, y] = at.split(',').map(Number) as [number, number];
                setHover([x, y]);
                cell(x, y);
              }}
              onPointerUp={() => (pressed.current = null)}
              onPointerCancel={() => (pressed.current = null)}
            >
              {arena.rows.flatMap((row, y) =>
                row.split('').map((c, x) => {
                  const tile = arena.legend[c]!,
                    spawn = arena.spawns.findIndex(([sx, sy]) => sx === x && sy === y);
                  return (
                    <g
                      key={`${x},${y}`}
                      data-arena-cell={`${x},${y}`}
                      role="button"
                      tabIndex={readOnly ? -1 : 0}
                      aria-label={`${x},${y} ${tile}${spawn >= 0 ? ` spawn ${spawn + 1}` : ''}`}
                      onMouseEnter={() => setHover([x, y])}
                      onFocus={() => setHover([x, y])}
                      // A press paints as it goes down; a click with no press behind it (a
                      // screen reader's, or a script's) paints here.
                      onClick={(e) => {
                        if (e.detail === 0) press(x, y);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          press(x, y);
                        }
                      }}
                    >
                      <title>
                        {tile} · {x},{y}
                      </title>
                      <rect
                        x={x}
                        y={y}
                        width="1"
                        height="1"
                        fill={tileColor(tileset, tile)}
                        stroke="#182333"
                        strokeWidth=".035"
                      />
                      {spawn >= 0 && (
                        <text
                          x={x + 0.5}
                          y={y + 0.7}
                          textAnchor="middle"
                          fontSize=".7"
                          fill="white"
                          stroke="#111"
                          strokeWidth=".03"
                        >
                          {spawn + 1}
                        </text>
                      )}
                    </g>
                  );
                }),
              )}
              {arena.groups?.map((g, i) => (
                <rect
                  key={i}
                  className="arena-footprint"
                  data-testid="arena-group"
                  x={g.x}
                  y={g.y}
                  width={g.width}
                  height={g.height}
                />
              ))}
              {!readOnly && hover && !brush.startsWith('spawn') && (
                <rect
                  className="arena-hover"
                  x={hover[0]}
                  y={hover[1]}
                  width={footprint[0]!.length}
                  height={footprint.length}
                />
              )}
            </svg>
            <p className="hint">
              Click a square to place the selected block/group, or drag to paint a block across
              squares. Groups start at the clicked square. Gold outlines mark saved group
              footprints. Groups cannot overlap existing terrain. Grass / erase removes a whole
              group. Spawn 1 is attacker; spawn 2 is defender.
            </p>
          </div>
          <aside className="editor-side-panel">
            <fieldset disabled={readOnly}>
              <legend>Building blocks</legend>
              <div className="block-palette">
                {tileset.tiles.map((t) => (
                  <button
                    className={brush === t.id ? 'active' : ''}
                    key={t.id}
                    onClick={() => setBrush(t.id)}
                    style={{ borderLeft: `8px solid ${tileColor(tileset, t.id)}` }}
                  >
                    {tileLabel(tileset, t.id)}
                  </button>
                ))}
              </div>
            </fieldset>
            <fieldset disabled={readOnly}>
              <legend>Groups & starting positions</legend>
              <div className="block-palette">
                {Object.keys(stamps).map((s) => (
                  <button
                    key={s}
                    className={brush === s ? 'active' : ''}
                    onClick={() => setBrush(s)}
                  >
                    {s}
                  </button>
                ))}
                {[0, 1].map((i) => (
                  <button
                    key={i}
                    className={brush === `spawn${i}` ? 'active' : ''}
                    onClick={() => setBrush(`spawn${i}`)}
                  >
                    Spawn {i + 1}
                  </button>
                ))}
              </div>
              <button onClick={() => setTurns((turns + 1) % 4)}>Rotate group: {turns * 90}°</button>
            </fieldset>
          </aside>
        </div>
      )}
    </section>
  );
}
